import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Download, Plus, Boxes, RefreshCw, Loader2, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { containersApi } from "../lib/api";
import { QFreshQRSticker, logoDataUrl } from "../components/QFreshQRSticker";
import { qrUrlFor } from "../lib/format";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Local helper so we don't need a roundtrip to mint print-time codes.
// Codes only become "claimed" the first time a container is logged against
// them, so generating them client-side is safe.
const genCode = () => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 8; i += 1) {
    out += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
  }
  return `QF-${out}`;
};

// US Letter — the "8.5 × 11" format the mock preview mirrors. All math is
// in mm to match jsPDF; the preview scales percentages off these values so
// the mock sheet, printed sheet, and sticker-size copy all stay in sync.
const PAPER = {
  widthMm: 215.9,   // 8.5"
  heightMm: 279.4,  // 11"
  marginXMm: 15,
  marginYMm: 6,     // tightened so 5 rows of 2.1" stickers still fit on 11"
  colGapMm: 5,
  rows: 5,          // max rows per column on the sheet
};
const STICKER_SIZE_MM = 53.34; // 2.1" cutout — QR stays a fixed 1.5" inside
const COUNT_OPTIONS = [1, 3, 5, 7, 10];

/**
 * Compute where each sticker sits on the sheet given how many the user picked.
 *
 * Rules (all layouts anchor to the top-left of the printable area so users
 * never waste vertical paper on centered-in-empty-space stickers):
 *   • 1 or 3 stickers  → horizontal row across the top, left-aligned.
 *   • 5 stickers       → single left column, top-down (never centered).
 *   • 7 or 10 stickers → left column (5) then right column (remainder),
 *                        both anchored to the top.
 *
 * Sticker size stays fixed at 53.34 mm (2.1") regardless of count so the printed
 * output is consistent (a "sticker" is always the same size in real life).
 * Returns { slots: [{xMm, yMm, sizeMm}], cols } — consumed by both the mock
 * preview (percentages) and the PDF export (mm).
 */
const layoutSlots = (count) => {
  const size = STICKER_SIZE_MM;
  const marginX = PAPER.marginXMm;
  const marginY = PAPER.marginYMm;
  const gap = PAPER.colGapMm;

  const slots = [];

  // Small counts → single top row, left-aligned. Saves paper: nothing sits
  // in the middle of a mostly-empty sheet.
  if (count <= 3) {
    for (let i = 0; i < count; i += 1) {
      const xMm = marginX + i * (size + gap);
      const yMm = marginY;
      slots.push({ xMm, yMm, sizeMm: size });
    }
    return { slots, cols: count };
  }

  // 5+ stickers → vertical column layout. We ALWAYS use 2-column geometry
  // so 5 stickers land in the left column (never centered), and 7/10 spill
  // to the right column at the same top anchor. This matches the mental
  // model of a label sheet where the left column is always filled first.
  const cols = 2;
  const usableW = PAPER.widthMm - 2 * marginX;
  const colWidth = (usableW - gap) / cols;
  for (let i = 0; i < count; i += 1) {
    const col = Math.floor(i / PAPER.rows);
    const row = i % PAPER.rows;
    const centerX = marginX + col * (colWidth + gap) + colWidth / 2;
    const centerY = marginY + size * (row + 0.5);
    slots.push({
      xMm: centerX - size / 2,
      yMm: centerY - size / 2,
      sizeMm: size,
    });
  }
  return { slots, cols: count > PAPER.rows ? 2 : 1 };
};

const PrintStickers = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // "blank" | "existing"
  const [count, setCount] = useState(5);
  const [blanks, setBlanks] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const pdfSourceRef = useRef(null);

  // Choose default mode AFTER the boxes fetch resolves: if the user already
  // has any boxes, "Reprint existing" is the intuitive default. Otherwise default to blanks.
  useEffect(() => {
    if (mode !== null) return;
    if (loadingBoxes) return;
    setMode(boxes.length > 0 ? "existing" : "blank");
  }, [boxes, loadingBoxes, mode]);

  // Regenerate blanks only when count/mode actually change.
  useEffect(() => {
    if (mode !== "blank") return;
    setBlanks((prev) => {
      const startingBox = boxes.reduce(
        (acc, b) => Math.max(acc, b.box_number || 0),
        0
      ) + 1;
      if (prev.length === count && prev.length > 0) return prev;
      return Array.from({ length: count }, (_, i) => ({
        code: genCode(),
        boxNumber: startingBox + i,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, mode]);

  useEffect(() => {
    setLoadingBoxes(true);
    containersApi
      .boxes()
      .then((b) => setBoxes(b || []))
      .catch(() => {})
      .finally(() => setLoadingBoxes(false));
  }, []);

  const regenerate = () => {
    if (mode !== "blank") return;
    const startingBox =
      boxes.reduce((acc, b) => Math.max(acc, b.box_number || 0), 0) + 1;
    setBlanks(
      Array.from({ length: count }, (_, i) => ({
        code: genCode(),
        boxNumber: startingBox + i,
      }))
    );
    toast.success("New batch generated");
  };

  const tilesToPrint = useMemo(() => {
    if (mode === "blank") return blanks;
    return boxes
      .filter((b) => b.qr_code && b.box_number)
      .map((b) => ({ code: b.qr_code, boxNumber: b.box_number, name: b.name }));
  }, [mode, blanks, boxes]);

  const layout = useMemo(
    () => layoutSlots(tilesToPrint.length),
    [tilesToPrint.length],
  );
  // Everything fits on ONE sheet now (max = 10 stickers → 2 columns × 5).
  const pageCount = 1;

  // Download a real PDF — no system print dialog. Renders each sticker tile
  // in a hidden off-screen container, snapshots it via html2canvas, then
  // places them on a single US Letter page using the shared layoutSlots().
  const handleDownloadPdf = async () => {
    if (tilesToPrint.length === 0) {
      toast.error("Nothing to download yet");
      return;
    }
    if (!pdfSourceRef.current) return;

    setGeneratingPdf(true);
    try {
      await new Promise((r) => setTimeout(r, 50));

      const tileNodes = pdfSourceRef.current.querySelectorAll(
        "[data-pdf-tile]"
      );
      if (tileNodes.length === 0) {
        toast.error("Could not build PDF — try again");
        return;
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "letter", // 8.5 × 11" — matches the mock preview + PAPER const
      });

      for (let i = 0; i < tileNodes.length; i += 1) {
        const node = tileNodes[i];
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(node, {
          backgroundColor: "#FFFFFF",
          scale: 2,
          useCORS: true,
          logging: false,
        });
        const img = canvas.toDataURL("image/jpeg", 0.92);

        const slot = layout.slots[i];
        pdf.addImage(img, "JPEG", slot.xMm, slot.yMm, slot.sizeMm, slot.sizeMm);

        // Draw the cutline directly with jsPDF's vector API. CSS
        // `border: dashed` doesn't survive html2canvas reliably, so we
        // overlay the dashes as a real PDF stroke — crisper on print,
        // and guaranteed to appear the same on every printer/PDF viewer.
        pdf.setDrawColor(138, 147, 166); // subtle slate #8A93A6
        pdf.setLineWidth(0.35);
        // Longer dash + shorter gap = clean rectangular dashes, not dots.
        pdf.setLineDashPattern([2.8, 1.4], 0);
        pdf.setLineCap("butt"); // sharp dash edges (no rounded ends)
        pdf.setLineJoin("miter"); // sharp 90° corners on the cutline rectangle
        const inset = 2; // ~2mm buffer so the cutline never crosses the QR/label
        pdf.rect(
          slot.xMm + inset,
          slot.yMm + inset,
          slot.sizeMm - inset * 2,
          slot.sizeMm - inset * 2,
        );
        pdf.setLineDashPattern([], 0);
      }

      const fileName = `QFresh-Stickers-${tilesToPrint.length}.pdf`;
      pdf.save(fileName);
      toast.success(`Downloaded ${fileName}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-10 max-w-3xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        data-testid="print-back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">
          Stickers
        </div>
        <h1 className="font-heading text-3xl font-bold text-[#2D3142] mt-1">
          Print your QR stickers
        </h1>
        <p className="text-sm text-[#4F5D75] mt-2 leading-relaxed">
          One US Letter sheet (8.5 × 11") with up to 10 stickers. Each sticker
          prints at <strong>≈ 2.1 × 2.1 inches</strong> (53 mm) with a <strong>1.5"
          QR</strong> centered inside — sized to fit the top or lid of any container.
        </p>
      </motion.div>

      {/* Mode picker */}
      <div className="mt-6 grid grid-cols-2 gap-2.5">
        <button
          type="button"
          onClick={() => setMode("existing")}
          data-testid="print-mode-existing"
          className={`tup-card p-4 text-left transition-colors border-2 ${
            mode === "existing"
              ? "border-[#277A82] bg-[#DCEDF0]/50"
              : "border-[#E2E0D8] bg-white hover:border-[#277A82]/60"
          }`}
        >
          <div className="flex items-center gap-2">
            <Boxes className={`w-4 h-4 ${mode === "existing" ? "text-[#1F5566]" : "text-[#8A93A6]"}`} />
            <span className={`font-semibold text-sm ${mode === "existing" ? "text-[#1F5566]" : "text-[#2D3142]"}`}>
              My boxes
            </span>
          </div>
          <p className="text-[11px] text-[#4F5D75] mt-2 leading-snug">
            {loadingBoxes
              ? "Loading…"
              : `Stickers for your ${boxes.length} existing box${boxes.length === 1 ? "" : "es"}.`}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setMode("blank")}
          data-testid="print-mode-blank"
          className={`tup-card p-4 text-left transition-colors border-2 ${
            mode === "blank"
              ? "border-[#277A82] bg-[#DCEDF0]/50"
              : "border-[#E2E0D8] bg-white hover:border-[#277A82]/60"
          }`}
        >
          <div className="flex items-center gap-2">
            <Plus className={`w-4 h-4 ${mode === "blank" ? "text-[#1F5566]" : "text-[#8A93A6]"}`} />
            <span className={`font-semibold text-sm ${mode === "blank" ? "text-[#1F5566]" : "text-[#2D3142]"}`}>
              Extra blanks
            </span>
          </div>
          <p className="text-[11px] text-[#4F5D75] mt-2 leading-snug">
            Fresh codes for future boxes. Claimed when first scanned.
          </p>
        </button>
      </div>

      {mode === "blank" && (
        <div
          className="mt-3 flex items-start gap-2 text-[11px] text-[#9C3E1D] bg-[#FBE9DD] border border-[#E07A5F]/40 rounded-xl px-3 py-2"
          data-testid="print-blank-warning"
        >
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            These are <strong>brand-new codes</strong>, NOT your existing boxes.
            To re-print stickers for boxes you've already logged, switch to <em>My boxes</em>.
          </span>
        </div>
      )}

      {/* Count picker (only for blank mode) */}
      {mode === "blank" && (
        <div className="mt-5 tup-card p-5" data-testid="print-blank-controls">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-heading text-base font-semibold text-[#2D3142]">
                How many stickers?
              </div>
              <div className="text-xs text-[#8A93A6] mt-0.5">
                All fit on a single 8.5 × 11" sheet.
              </div>
            </div>
            <button
              type="button"
              onClick={regenerate}
              data-testid="print-regenerate-btn"
              className="text-[#277A82] hover:text-[#1F5566] flex items-center gap-1 text-xs font-semibold"
            >
              <RefreshCw className="w-3.5 h-3.5" /> New batch
            </button>
          </div>
          <div className="mt-4 grid grid-cols-5 gap-2">
            {COUNT_OPTIONS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setCount(n)}
                data-testid={`print-count-${n}`}
                className={`rounded-xl py-2 text-sm font-bold transition-colors ${
                  count === n
                    ? "brand-gradient-bg text-white shadow-md"
                    : "bg-white border border-[#E2E0D8] text-[#4F5D75] hover:border-[#277A82]/60"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={handleDownloadPdf}
        disabled={tilesToPrint.length === 0 || generatingPdf}
        data-testid="download-pdf-btn"
        className="w-full h-14 mt-6 brand-gradient-bg text-white text-base font-semibold hover:opacity-95"
      >
        {generatingPdf ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Building PDF…
          </>
        ) : (
          <>
            <Download className="w-5 h-5 mr-2" />
            Download PDF — {tilesToPrint.length} sticker{tilesToPrint.length === 1 ? "" : "s"}
            {pageCount > 1 ? ` (${pageCount} pages)` : ""}
          </>
        )}
      </Button>

      <div className="mt-3 text-[11px] text-[#8A93A6] text-center leading-relaxed">
        Saves directly to your Downloads folder. Open the PDF and print from there
        with <strong>Scale: 100%</strong> for accurate sizing.
      </div>

      {/* On-screen preview — one mock 8.5×11" sheet that mirrors the PDF.
          Updates in real time as the user changes the sticker count so they
          get a true feel for the printed layout before hitting Download. */}
      <div className="mt-8">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6] mb-3">
          Preview
        </div>
        {tilesToPrint.length === 0 ? (
          <div className="tup-card p-8 text-center text-sm text-[#4F5D75]">
            {mode === "existing"
              ? "No boxes yet — log a container first."
              : "Pick a count to generate stickers."}
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div
              data-testid="print-preview-sheet"
              className="relative bg-white border border-[#E2E0D8] shadow-[0_2px_16px_rgba(45,49,66,0.12)] rounded-sm"
              style={{
                width: "100%",
                maxWidth: "340px",
                aspectRatio: `${PAPER.widthMm} / ${PAPER.heightMm}`,
              }}
            >
              {layout.slots.map((slot, idx) => {
                const t = tilesToPrint[idx];
                if (!t) return null;
                const leftPct = (slot.xMm / PAPER.widthMm) * 100;
                const topPct = (slot.yMm / PAPER.heightMm) * 100;
                const wPct = (slot.sizeMm / PAPER.widthMm) * 100;
                const hPct = (slot.sizeMm / PAPER.heightMm) * 100;
                return (
                  <div
                    key={`slot-${idx}`}
                    data-testid={`preview-slot-${idx}`}
                    style={{
                      position: "absolute",
                      left: `${leftPct}%`,
                      top: `${topPct}%`,
                      width: `${wPct}%`,
                      height: `${hPct}%`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/* Sharp dashed cutline drawn as an SVG rect so we get
                        crisp rectangular dashes on every browser — CSS
                        `border: dashed` renders as tiny anti-aliased dots
                        at 1px on hi-DPI displays. */}
                    <svg
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                      }}
                      aria-hidden="true"
                    >
                      <rect
                        x="1.2"
                        y="1.2"
                        width="97.6"
                        height="97.6"
                        fill="none"
                        stroke="#8A93A6"
                        strokeWidth="1.2"
                        strokeDasharray="3.5 2"
                        strokeLinecap="butt"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                    {/* Centered QR sized as a FIXED PERCENTAGE of the slot
                        so it always renders at exactly 1.5"/2.1" of the
                        cutout (71.43%), regardless of viewport width. The
                        Q-with-number logo is embedded in the middle via
                        `imageSettings.excavate` so it stays scannable. */}
                    <div
                      style={{
                        width: "71.43%",
                        aspectRatio: "1 / 1",
                        display: "flex",
                      }}
                    >
                      <QRCodeSVG
                        value={qrUrlFor(t.code)}
                        size={200}
                        level="H"
                        bgColor="#FFFFFF"
                        fgColor="#1F5566"
                        marginSize={0}
                        imageSettings={{
                          src: logoDataUrl(t.boxNumber),
                          height: 44,
                          width: 44,
                          excavate: true,
                        }}
                        style={{ width: "100%", height: "100%", display: "block" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <p
              className="text-[11px] text-[#8A93A6] mt-3 text-center leading-snug max-w-[300px]"
              data-testid="preview-disclaimer"
            >
              Mockup shown at 8.5 × 11" size. For the cleanest peel, print on
              adhesive label paper (any full-sheet 8.5×11 label works) — then
              cut along the dashed lines.
            </p>
          </div>
        )}
      </div>

      {/* Off-screen tiles used as the source for the PDF snapshot.
          Positioned absolutely off-canvas so they render with real layout
          (html2canvas needs a laid-out node) but never appear on screen.
          The caption is rendered manually here (showCaption=false on the
          sticker) so we can use 2× larger text in the PDF without affecting
          the in-app sticker component used on other pages. */}
      <div
        ref={pdfSourceRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: "-10000px",
          width: "560px",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        {tilesToPrint.map((t) => (
          <div
            key={`pdf-${t.code}`}
            data-pdf-tile
            style={{
              // Fixed square footprint so the cutline reads as a clean
              // square cutout regardless of caption length.
              width: "520px",
              height: "520px",
              boxSizing: "border-box",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* size=372 → 372/520 × 53.34mm = 38.15mm = 1.502" printed.
                Exact 1.5" would need size=371.43 (non-integer). Rounded up
                so the printed QR is never UNDER 1.5". The QFreshQRSticker
                keeps the Q-with-box-number logo excavated into the middle. */}
            <QFreshQRSticker
              code={t.code}
              boxNumber={t.boxNumber}
              size={372}
              showCaption={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrintStickers;
